import { React, useRef, useState, useCallback } from 'react'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'
import { Box, Button, styled } from '@mui/material'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Currency } from 'react-intl-number-format'
import PropTypes from 'prop-types'
import StyledLink from '../StyledLink'
import { DiscountLabel, BrandName, StockLabel } from '../styledComponents/StyledItemComponents'
import { useAppContext } from '../../context/AppProvider'
import { useCart } from '../../hooks/useCart'
import { useFavorites } from '../../hooks/useFavorites'
import { debounce } from 'lodash'
import ItemAvatar from '../../icons/tools-hardware-128.png'
import { SHIPPING_PRICE, getCollectionBadge, getDisplayBrandName, getEffectivePrice, getStockLabel } from '../../utils/itemPresentation'

const getFavoriteButtonSx = (active) => ({
  borderRadius: '18px',
  textTransform: 'none',
  minWidth: '96px',
  borderColor: '#e20613',
  color: active ? '#FFFFFF' : '#e20613',
  backgroundColor: active ? '#e20613' : 'transparent',
  '&:hover': {
    borderColor: '#c90012',
    backgroundColor: active ? '#c90012' : 'rgba(226, 6, 19, 0.08)'
  }
})

const ProductCard = ({ item }) => {
  const { state } = useAppContext()
  const { addItemToFavorites, removeItemFromFavorites } = useFavorites()
  const { updateCartItem } = useCart()
  const itemData = useRef(item)
  const [addedToCart, setAddedToCart] = useState(false)

  const isItemInFavorites = state.favorites.results?.some(favoriteItem => favoriteItem._id === item._id)

  const ProductName = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    lineHeight: '1.55',
    fontWeight: 600,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    WebkitLineClamp: 2,
    textOverflow: 'ellipsis'
  }))

  const collectionBadge = getCollectionBadge(item)
  const stockLabel = getStockLabel(item)
  const finalPrice = getEffectivePrice(item)
  const displayBrandName = getDisplayBrandName(item)

  const debouncedCartAction = useCallback(debounce((action, data) => {
    action(data)
    setAddedToCart(false)
  }, 1000, {
    leading: false,
    trailing: true
  }), [])

  const handleAddToCart = () => {
    setAddedToCart(true)
    debouncedCartAction(updateCartItem, itemData)
  }

  const debouncedFavoriteAction = useCallback(debounce((action, data) => {
    action(data)
  }, 200, {
    leading: false,
    trailing: true
  }), [])

  const handleAddToFavorites = () => {
    const action = isItemInFavorites ? removeItemFromFavorites : addItemToFavorites
    debouncedFavoriteAction(action, itemData)
  }

  return (
    <Card item_id={itemData.current._id} sx={{ width: '250px', margin: '20px 0', paddingBottom: '16px', minHeight: '470px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ minHeight: '44px', display: 'flex', alignItems: 'center', px: 2, pt: 2 }}>
        {item.brand?.logo
          ? (
            <Box
              component='img'
              src={item.brand.logo}
              alt={item.brand?.name || 'Brand logo'}
              sx={{ width: '88px', height: '28px', objectFit: 'contain', objectPosition: 'left center' }}
            />
            )
          : displayBrandName ? <BrandName>{displayBrandName}</BrandName> : null}
      </Box>
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component='img'
          fetchpriority='high'
          loading='eager'
          sx={{ height: '200px', width: '100%', objectFit: 'contain', padding: item.cover ? '0' : '70px' }}
          image={item.cover || ItemAvatar}
          title={item.name}
        />
      </Box>
      <CardContent sx={{ paddingBottom: 0, flexGrow: 1 }}>
        {item && (
          <StyledLink to={{ pathname: `/items/${item._id}` }}>
            <ProductName>
              {item.name}
            </ProductName>
          </StyledLink>
        )}
        {collectionBadge && (
          <DiscountLabel sx={{ top: 'auto', left: 0, mt: 1.25 }}>
            {collectionBadge}
          </DiscountLabel>
        )}
        <Box sx={{ marginTop: '16px' }}>
          <Typography variant='h6' sx={{ color: 'accent.main', fontSize: '18px', fontWeight: 600 }}>
            <Currency locale='ro-RO' currency='RON'>
              {finalPrice}
            </Currency>
          </Typography>
          <Typography variant='body2' sx={{ color: 'fontColor.medium', marginTop: '6px' }}>
            Transport: {SHIPPING_PRICE} lei
          </Typography>
        </Box>
      </CardContent>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, paddingX: '16px' }}>
        <StockLabel sx={{ left: 0, paddingLeft: '10px' }}>
          {stockLabel}
        </StockLabel>
        <CardActions sx={{ display: 'flex', justifyContent: 'space-between', padding: 0, gap: 1 }}>
          <Button
            aria-label='favorite'
            onClick={handleAddToFavorites}
            variant={isItemInFavorites ? 'contained' : 'outlined'}
            startIcon={isItemInFavorites ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            sx={getFavoriteButtonSx(isItemInFavorites)}
          >
            Favorite
          </Button>
          <Button
            aria-label='add to cart'
            onClick={handleAddToCart}
            variant='contained'
            startIcon={addedToCart ? <CheckCircleIcon /> : null}
            sx={{
              borderRadius: '18px',
              textTransform: 'none',
              minWidth: '96px',
              backgroundColor: '#6f6f6f',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#5f5f5f'
              }
            }}
          >
            {addedToCart ? 'Adăugat' : 'ADAUGĂ'}
          </Button>
        </CardActions>
      </Box>
    </Card>
  )
}

ProductCard.propTypes = {
  item: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number,
    final_price: PropTypes.number,
    stock: PropTypes.number,
    discount: PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.number,
      price: PropTypes.number
    }),
    brand: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string
    }),
    category: PropTypes.shape({
      name: PropTypes.array,
      link: PropTypes.array
    }),
    cover: PropTypes.string,
    images: PropTypes.array
  }).isRequired
}
export default ProductCard
